// SUMMARY: Isomorphic (server + client) serialize/parse for the in-chat document wizard form. The wizard
// is STATELESS over history: the "collect" assistant turn embeds the form spec AND the resolved state
// (docType, requirements, sources) in a fenced ```evuno-form block inside its message content. The chat
// renderer turns that block into a <DocumentForm>; the chat route reads it back on the next turn to
// generate without re-researching. No server-only imports here so both sides can use it.

export type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'select'

export interface FormField {
	key: string
	label: string
	type: FieldType
	required?: boolean
	placeholder?: string
	/** options for a select field */
	options?: string[]
	/** prefilled value the orchestrator already knows from the conversation */
	value?: string
}

/** Hidden wizard state carried across turns inside the form block (not rendered to the user). */
export interface WizardState {
	docType: string
	isLegal: boolean
	jurisdiction: string
	/** distilled legal requirements (Linkup sourced answer), when researched */
	requirements?: string
	sources?: { name: string; url: string }[]
}

export interface WizardForm {
	/** short human-readable line shown above the form */
	intro: string
	/** form card heading */
	title: string
	fields: FormField[]
	state: WizardState
}

const FENCE = 'evuno-form'
// EXAMPLE: matches the ```evuno-form ... ``` block and captures its base64 payload
const FORM_BLOCK = /```evuno-form\s*\n([\s\S]*?)\n```/

// The payload is base64 (ASCII) so the markdown sanitizer/renderer can never corrupt the embedded JSON
// (e.g. stripping a "$...$" run). UTF-8-safe via TextEncoder/TextDecoder, available on server + browser.
function toBase64(text: string): string {
	return btoa(String.fromCharCode(...new TextEncoder().encode(text)))
}
function fromBase64(b64: string): string {
	return new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)))
}

/** Encode a wizard form into an assistant message: a visible intro line + a hidden fenced spec block. */
export function encodeForm(form: WizardForm): string {
	const payload = toBase64(JSON.stringify({ version: 1, title: form.title, fields: form.fields, state: form.state }))
	return `${form.intro.trim()}\n\n\`\`\`${FENCE}\n${payload}\n\`\`\``
}

interface FormPayload {
	title?: string
	fields?: FormField[]
	state?: WizardState
}

function decodePayload(raw: string): FormPayload | null {
	try {
		return JSON.parse(fromBase64(raw.trim())) as FormPayload
	} catch {
		return null
	}
}

/** Whether a message contains a wizard form block (used to detect a wizard continuation turn). */
export function messageHasForm(content: string): boolean {
	return FORM_BLOCK.test(content || '')
}

/** Parse a wizard form out of a full message; null if absent or malformed. */
export function parseForm(content: string): WizardForm | null {
	const match = FORM_BLOCK.exec(content || '')
	if (!match) return null
	const data = decodePayload(match[1])
	if (!data?.state || !Array.isArray(data.fields)) return null
	const intro = content.slice(0, match.index).trim()
	return { intro, title: data.title || '', fields: data.fields, state: data.state }
}

/** The embedded wizard state from a (form) message, or null. */
export function parseWizardState(content: string): WizardState | null {
	return parseForm(content)?.state ?? null
}

/**
 * Decode just the (base64) payload of an `evuno-form` fenced block — what the chat renderer receives as the
 * code-block body, without the surrounding intro. Returns the title/fields/state, or null if malformed.
 */
export function parseFormPayload(payload: string): Pick<WizardForm, 'title' | 'fields' | 'state'> | null {
	const data = decodePayload(payload)
	if (!data?.state || !Array.isArray(data.fields)) return null
	return { title: data.title || '', fields: data.fields, state: data.state }
}
